/**
 * Shared status + schema helpers for the Human Call tab models.
 *
 * These pure helpers are consumed by both the guided **Call Script** model
 * ({@link ./scriptModel}) and the "All fields" board ({@link ./fieldBoardModel}):
 *   - {@link computeStatus} derives a field's live {@link FieldStatus};
 *   - {@link isEmptyValue} / {@link readRelativePath} read live output values;
 *   - {@link findTopLevelField} / {@link findEntityField} / {@link resolveTemplateField}
 *     locate schema {@link FormField} defs.
 *
 * This module is PURE (no React, no I/O) so it can be unit-tested in isolation.
 */
import type { FieldGroup, FormField } from '@/types/formSchema';

export type FieldStatus = 'captured' | 'prefilled' | 'missing' | 'heard' | 'optional';

/** Empty per the same convention used by nestedAccordion / dispositionCompleteness. */
export function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Read a value from an object by a dot-separated relative path. A segment may carry a
 * trailing array index (`denials[0]`) so a double-indexed key
 * (`drug[0].denials[0].reason`, from a nested-record array) resolves; plain segments
 * behave unchanged.
 */
export function readRelativePath(source: unknown, relativePath: string): unknown {
  if (!relativePath) return source;
  let current: unknown = source;
  for (const segment of relativePath.split('.')) {
    if (current === undefined || current === null || typeof current !== 'object') {
      return undefined;
    }
    const indexed = /^(.+?)\[(\d+)\]$/.exec(segment);
    if (indexed) {
      const arr = (current as Record<string, unknown>)[indexed[1]];
      current = Array.isArray(arr) ? arr[Number(indexed[2])] : undefined;
    } else {
      current = (current as Record<string, unknown>)[segment];
    }
  }
  return current;
}

/** Find a top-level FormField (by id) across all output schema groups. */
export function findTopLevelField(groups: FieldGroup[], fieldId: string): FormField | undefined {
  for (const group of groups) {
    for (const field of group.fields) {
      if (field.id === fieldId) return field;
    }
  }
  return undefined;
}

/** Find the entity's array FormField template (e.g. `drug`) across all groups. */
export function findEntityField(groups: FieldGroup[], entityKey: string): FormField | undefined {
  return findTopLevelField(groups, entityKey);
}

/**
 * Resolve a top-level field by a dot-separated path (e.g. `coverage.coverage_status`
 * into a top-level singular object), descending nested `fields`. A single-segment
 * path is a plain top-level id. Returns undefined when any segment is missing.
 */
export function findTopLevelFieldByPath(groups: FieldGroup[], path: string): FormField | undefined {
  const dot = path.indexOf('.');
  if (dot < 0) return findTopLevelField(groups, path);
  const top = findTopLevelField(groups, path.slice(0, dot));
  return top ? resolveTemplateField(top, path.slice(dot + 1)) : undefined;
}

/**
 * Descend an entity's nested field template to resolve a relative path
 * (e.g. "coverage.coverage_status") to its leaf FormField def.
 */
export function resolveTemplateField(
  template: FormField | undefined,
  relativePath: string,
): FormField | undefined {
  if (!template?.fields || !relativePath) return undefined;
  const segments = relativePath.split('.');
  let fields: FormField[] | undefined = template.fields;
  let resolved: FormField | undefined;
  for (const segment of segments) {
    // A segment may carry a trailing array index (`denials[0]`) for a nested-record array;
    // the template is index-agnostic, so match on the bare field id.
    const id = segment.replace(/\[\d+\]$/, '');
    resolved = fields?.find(f => f.id === id);
    if (!resolved) return undefined;
    fields = resolved.fields;
  }
  return resolved;
}

export function computeStatus(
  key: string,
  value: unknown,
  missingFieldSet: Set<string>,
  heardFieldSet: Set<string>,
  sessionEditedKeys: Set<string>,
): FieldStatus {
  // Value-first so the status updates the INSTANT a value is entered. The script
  // model rebuilds on every keystroke (workingOutput is a dependency), so a
  // non-empty value flips to captured/prefilled immediately — it does not wait
  // for the disposition engine's commit-gated recompute to drop the key from
  // `missingFieldSet`. `missing`/`optional`/`heard` therefore only apply to EMPTY fields.
  if (!isEmptyValue(value)) {
    return sessionEditedKeys.has(key) ? 'captured' : 'prefilled';
  }
  // A field the agent heard but hasn't yet committed — acknowledged on-recording,
  // not yet missing (won't advance currentUnitId past this point).
  if (heardFieldSet.has(key)) return 'heard';
  if (missingFieldSet.has(key)) return 'missing';
  return 'optional';
}
